import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadAuth } from '../auth';

const API_BASE_URL = 'https://shop.neoteeforge.com/neotee-backend';

const SellerDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [earnings, setEarnings] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [profile, setProfile] = useState(null);
    const [payoutAmount, setPayoutAmount] = useState('');
    
    const navigate = useNavigate();
    const auth = loadAuth();
    
    useEffect(() => {
        if (!auth?.token) {
            navigate('/login');
            return;
        }
        
        fetchDashboardData();
    }, []);
    
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            
            // Fetch seller dashboard
            const response = await fetch(`${API_BASE_URL}/api/seller/dashboard`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (data.success) {
                setDashboardData(data.dashboard);
                setProfile(data.dashboard.profile);
                setProducts(data.dashboard.products || []);
                setOrders(data.dashboard.recent_orders || []);
                setEarnings(data.dashboard.recent_earnings || []);
            }
            
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const fetchProducts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/seller/products`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            if (data.success) {
                setProducts(data.products);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };
    
    const fetchOrders = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/seller/orders`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            if (data.success) {
                setOrders(data.orders);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
        }
    };
    
    const fetchEarnings = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/seller/earnings`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            if (data.success) {
                setEarnings(data.earnings);
            }
        } catch (error) {
            console.error('Error fetching earnings:', error);
        }
    };
    
    const fetchPayouts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/seller/payouts`, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            if (data.success) {
                setPayouts(data.payouts);
            }
        } catch (error) {
            console.error('Error fetching payouts:', error);
        }
    };
    
    const handleRequestPayout = async () => {
        if (!payoutAmount || payoutAmount <= 0) {
            alert('Please enter a valid amount');
            return;
        }
        
        if (parseFloat(payoutAmount) > (profile?.available_balance || 0)) {
            alert('Amount exceeds available balance');
            return;
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/seller/request-payout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ amount: parseFloat(payoutAmount) })
            });
            
            const data = await response.json();
            if (data.success) {
                alert('Payout request submitted successfully');
                setPayoutAmount('');
                fetchDashboardData();
                fetchPayouts();
            } else {
                alert(data.error || 'Failed to request payout');
            }
        } catch (error) {
            console.error('Error requesting payout:', error);
            alert('Failed to request payout');
        }
    };
    
    const handleCreateProduct = () => {
        navigate('/seller/products/create');
    };
    
    const handleEditProduct = (productId) => {
        navigate(`/seller/products/edit/${productId}`);
    };
    
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };
    
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };
    
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 p-4">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            {[1,2,3,4].map(i => (
                                <div key={i} className="h-32 bg-gray-300 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    if (!dashboardData) {
        return (
            <div className="min-h-screen bg-gray-100 p-4">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">Seller Dashboard</h2>
                        <p className="text-gray-600">Failed to load dashboard data</p>
                    </div>
                </div>
            </div>
        );
    }
    
    const stats = dashboardData.stats || {};
    
    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Seller Dashboard</h1>
                            <p className="text-gray-600 mt-1">
                                Welcome back, {profile?.business_name || 'Seller'}!
                            </p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                profile?.status === 'approved' 
                                    ? 'bg-green-100 text-green-800'
                                    : profile?.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                            }`}>
                                {profile?.status?.toUpperCase()}
                            </span>
                            <button
                                onClick={handleCreateProduct}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                            >
                                Add New Product
                            </button>
                        </div>
                    </div>
                    
                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white p-6 rounded-lg shadow border">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-blue-100 p-3 rounded-lg">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                                    <p className="text-2xl font-semibold text-gray-900">
                                        {formatCurrency(stats.total_earnings)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow border">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-green-100 p-3 rounded-lg">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Available Balance</p>
                                    <p className="text-2xl font-semibold text-gray-900">
                                        {formatCurrency(stats.available_balance)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow border">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-purple-100 p-3 rounded-lg">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Products</p>
                                    <p className="text-2xl font-semibold text-gray-900">
                                        {stats.total_products || 0}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow border">
                            <div className="flex items-center">
                                <div className="flex-shrink-0 bg-orange-100 p-3 rounded-lg">
                                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Orders</p>
                                    <p className="text-2xl font-semibold text-gray-900">
                                        {stats.total_orders || 0}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                        {['overview', 'products', 'orders', 'earnings', 'payouts', 'settings'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === tab
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </nav>
                </div>
                
                {/* Tab Content */}
                <div className="bg-white rounded-lg shadow">
                    {activeTab === 'overview' && (
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
                            
                            {/* Recent Orders */}
                            <div className="mb-8">
                                <h4 className="text-md font-medium text-gray-700 mb-3">Recent Orders</h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead>
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Order ID
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Product
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Customer
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Amount
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Date
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {orders.slice(0, 5).map((order) => (
                                                <tr key={order.id}>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        #{order.id}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                        {order.product_title}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                        {order.customer_name}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {formatCurrency(order.item_price * order.quantity)}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                                            order.status === 'completed'
                                                                ? 'bg-green-100 text-green-800'
                                                                : order.status === 'pending'
                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                        {formatDate(order.created_at)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            
                            {/* Payout Request */}
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h4 className="text-md font-medium text-gray-700 mb-3">Request Payout</h4>
                                <div className="flex items-center space-x-4">
                                    <div className="flex-1">
                                        <input
                                            type="number"
                                            value={payoutAmount}
                                            onChange={(e) => setPayoutAmount(e.target.value)}
                                            placeholder="Enter amount"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                    <button
                                        onClick={handleRequestPayout}
                                        disabled={!payoutAmount || parseFloat(payoutAmount) <= 0}
                                        className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Request Payout
                                    </button>
                                </div>
                                <p className="text-sm text-gray-500 mt-2">
                                    Available balance: {formatCurrency(stats.available_balance)} | 
                                    Minimum payout: ₹100
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'products' && (
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-medium text-gray-900">My Products</h3>
                                <button
                                    onClick={handleCreateProduct}
                                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                                >
                                    Add New Product
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {products.map((product) => (
                                    <div key={product.id} className="border rounded-lg overflow-hidden shadow-sm">
                                        <div className="p-4">
                                            <div className="flex justify-between items-start mb-3">
                                                <h4 className="font-medium text-gray-900 truncate">
                                                    {product.title}
                                                </h4>
                                                <span className={`px-2 py-1 text-xs rounded ${
                                                    product.status === 1
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {product.status === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500 mb-2">
                                                by {product.author}
                                            </p>
                                            <p className="text-lg font-semibold text-gray-900 mb-3">
                                                {formatCurrency(product.price)}
                                            </p>
                                            <div className="flex justify-between items-center text-sm text-gray-500">
                                                <span>Stock: {product.stock_quantity}</span>
                                                <span>Sales: {product.sales_count || 0}</span>
                                            </div>
                                            <div className="mt-4 flex space-x-2">
                                                <button
                                                    onClick={() => handleEditProduct(product.id)}
                                                    className="flex-1 bg-indigo-50 text-indigo-700 px-3 py-2 rounded text-sm hover:bg-indigo-100 transition"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => {/* Handle delete */}}
                                                    className="flex-1 bg-red-50 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-100 transition"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'orders' && (
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-6">Orders</h3>
                            
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Order ID
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Product
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Customer
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Quantity
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {orders.map((order) => (
                                            <tr key={order.id}>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    #{order.id}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {order.product_title}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {order.customer_name}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {order.quantity}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {formatCurrency(order.item_price * order.quantity)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                                        order.status === 'completed'
                                                            ? 'bg-green-100 text-green-800'
                                                            : order.status === 'pending'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {formatDate(order.created_at)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'earnings' && (
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-6">Earnings</h3>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                                <div className="bg-gray-50 p-6 rounded-lg">
                                    <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                                        {formatCurrency(stats.total_earnings)}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-6 rounded-lg">
                                    <p className="text-sm font-medium text-gray-600">Available Balance</p>
                                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                                        {formatCurrency(stats.available_balance)}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-6 rounded-lg">
                                    <p className="text-sm font-medium text-gray-600">Commission Rate</p>
                                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                                        {profile?.commission_rate || 15}%
                                    </p>
                                </div>
                            </div>
                            
                            <h4 className="text-md font-medium text-gray-700 mb-3">Recent Earnings</h4>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total Sales
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Commission
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Your Earnings
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {earnings.map((earning, index) => (
                                            <tr key={index}>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {formatDate(earning.date)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {formatCurrency(earning.total_earnings + earning.total_commission)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {formatCurrency(earning.total_commission)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-green-600 font-medium">
                                                    {formatCurrency(earning.total_earnings)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'payouts' && (
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-medium text-gray-900">Payouts</h3>
                                <button
                                    onClick={() => setActiveTab('overview')}
                                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
                                >
                                    Request New Payout
                                </button>
                            </div>
                            
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Payout ID
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Net Amount
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Method
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {payouts.map((payout) => (
                                            <tr key={payout.id}>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    #{payout.id}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {formatCurrency(payout.payout_amount)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {formatCurrency(payout.net_amount)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {payout.payout_method}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                                        payout.status === 'completed'
                                                            ? 'bg-green-100 text-green-800'
                                                            : payout.status === 'pending'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : payout.status === 'processing'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {payout.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {formatDate(payout.created_at)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'settings' && (
                        <div className="p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-6">Seller Settings</h3>
                            
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h4 className="text-md font-medium text-gray-700 mb-4">Business Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Business Name
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.business_name}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Business Type
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.business_type}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Tax ID
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.tax_id}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Commission Rate
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={`${profile?.commission_rate}%`}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-6">
                                <h4 className="text-md font-medium text-gray-700 mb-4">Bank Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Bank Name
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.bank_name}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Account Number
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.bank_account_number}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            IFSC Code
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.bank_ifsc}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            UPI ID
                                        </label>
                                        <input
                                            type="text"
                                            defaultValue={profile?.upi_id}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SellerDashboard;